import React, {
    forwardRef,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useRef,
    RefObject,
} from 'react';

interface PdfTextLayerProps {
    pageNum: number;
    registerTextLayer?: (pageNum: number, ref: RefObject<HTMLDivElement>) => void;
    unregisterTextLayer?: (pageNum: number) => void;
}

/**
 * 通过 ref 暴露的命令式接口：
 * onRenderSuccess 触发时，由 PdfPage 调用 registerIfMounted，将文本层注册到搜索系统。
 */
export interface PdfTextLayerHandle {
    registerIfMounted: (pageNum: number) => void;
}

/**
 * PDF 文本层组件
 *
 * 职责：
 * 1. 渲染文本层容器 div（搜索高亮覆盖层）
 * 2. 页码切换时清空旧页的 span/mark（防止 usePdfSearch 跳过重新挂载）
 * 3. 卸载时注销文本层（从搜索系统移除）
 * 4. 暴露 registerIfMounted 供父组件在渲染完成后调用
 *
 * 注：PDF.js page.cleanup() 属于渲染资源管理，由 PdfPage 负责，不在此处调用。
 */
export const PdfTextLayer = forwardRef<PdfTextLayerHandle, PdfTextLayerProps>(
    ({ pageNum, registerTextLayer, unregisterTextLayer }, ref) => {
        const divRef = useRef<HTMLDivElement>(null);

        useImperativeHandle(ref, () => ({
            registerIfMounted: (pNum: number) => {
                if (registerTextLayer && divRef.current) {
                    registerTextLayer(pNum, divRef);
                }
            },
        }));

        // 翻页时清空旧页内容，防止 usePdfSearch 因 childElementCount > 0 跳过重新挂载
        useLayoutEffect(() => {
            if (divRef.current) {
                divRef.current.innerHTML = '';
            }
        }, [pageNum]);

        // 卸载时注销文本层（从搜索系统移除）
        useEffect(() => {
            return () => {
                unregisterTextLayer?.(pageNum);
            };
        }, [pageNum, unregisterTextLayer]);

        return (
            <div
                ref={divRef}
                className="pdf-content-text-search"
                style={{
                    pointerEvents: 'none', // 禁用指针事件，避免阻挡页面交互
                    position: 'absolute',
                    top: 0,
                    // left 值由 mountTextLayerToDom 动态设置，基于 canvas/svg 的实际位置
                }}
            />
        );
    },
);
